import { Row, Col, Card, Table, Button, Input, Tooltip, Switch, Tag as AntTag } from "antd";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import ShowTotal from "../../components/ShowTotal";
import ConfirmationBox from "../../components/ConfirmationBox";
import apiPath from "../../constants/apiPath";
import { formatDate, formatDateTime } from "../../helper/DateHelper";
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { getSortOrder } from "../../constants/conts";
const Search = Input.Search;

function Index() {

  const sectionName   =   "Referral";
  const routeName     =   "referral";

  const api = {
    status    :   apiPath.statusReferral,
    addEdit   :   apiPath.addEditReferral,
    list      :   apiPath.listReferral
  }

  const location        =   useLocation();
  const navigate        =   useNavigate();

  const query           =   new URLSearchParams(location.search);
  const initialPage     =   parseInt(query.get("page")) || 1;
  const initialPageSize =   parseInt(query.get("pageSize")) || 20;
  const initialSearch   =   query.get("search") || "";
  const initialSort     =   query.get("sort") || "";

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
  });

  const [searchText, setSearchText]   =   useState(initialSearch);
  const [sortBy, setSortBy]           =   useState(initialSort);

  const { request }                   =   useRequest()
  const [list, setList]               =   useState([])
  const { showConfirm }               =   ConfirmationBox()
  const [loading, setLoading]         =   useState(false)
  const [refresh, setRefresh]         =   useState(false)
  const [filters, setFilters]         =   useState({});
  const debouncedSearchText           =   useDebounce(searchText, 500);

  const columns = [
    {
      title: "Referral Code",
      dataIndex: "referral_code",
      key: "referral_code",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "referral_code"),
      render: (_, { referral_code }) => {
        return (
          referral_code ? <AntTag color="purple">{referral_code}</AntTag> : '-'
        );
      },
    },
    {
      title: "Brand",
      dataIndex: ["brand", "name"],
      key: "brand.name",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "brand.name"),
      render: (_, { brand }) => {
        return (
          brand?.name ? <span className="cap">{brand.name}</span> : '-'
        );
      },
    },
    {
      title: "Referrer Type",
      dataIndex: "referrer_type",
      key: "referrer_type",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "referrer_type"),
      render: (_, { referrer_type }) => {
        return (
          referrer_type ? <span className="cap">{referrer_type}</span> : '-'
        );
      },
    },
    {
      title: "Name of Referrer",
      dataIndex: "referrer_name",
      key: "referrer_name",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "referrer_name"),
      render: (_, { referrer_name }) => {
        return (
          referrer_name ? <span className="cap">{referrer_name}</span> : '-'
        );
      },
    },
    {
      title: "Contact Details",
      key: "contact_detail",
      align: "center",
      render: (_, { contact_detail }) => {

        if (!contact_detail || !contact_detail.length) return '-';

        return (
          <Tooltip
            color={"purple"}
            title={
              <>
                {contact_detail.map((item, index) => (
                  <div key={index}>{`${item?.label ? item.label : '-'} : ${item?.value ? item.value : '-'}`}</div>
                ))}
              </>
            }
          >
            <AntTag>{`${contact_detail.length} ${contact_detail.length > 1 ? 'Details' : 'Detail'}`}</AntTag>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      key: "is_active",
      align: "center",
      filters: [
        {
          text: 'Active',
          value: true,
        },
        {
          text: 'Inactive',
          value: false,
        },
      ],
      filteredValue: filters?.is_active || null,
      render: (_, { is_active, deactivated_at, _id }) => {
        return (
          <>
            <Switch
              checked = {is_active}
              checkedChildren = {<CheckOutlined />}
              unCheckedChildren = {<CloseOutlined />}
              loading = {loading}
              onChange = {(checked) =>
                showConfirm({
                  record    : _id,
                  path      : api.status,
                  onLoading : () => setLoading(true),
                  onSuccess : () => setRefresh((prev) => !prev),
                  is_active : checked,
                })
              }
              className={is_active ? "custom-switch-checked" : ""}
            />

            {/* The date on which the referral was deactivated, only kept for inactive referrals */}
            {!is_active && deactivated_at &&
              <Tooltip title={`Deactivated on ${formatDateTime(deactivated_at)}`} color={"purple"}>
                <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4, whiteSpace: "nowrap" }}>
                  {formatDate(deactivated_at)}
                </div>
              </Tooltip>
            }
          </>
        );
      },
    },
    {
      title: "Created On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => {
        return (
          formatDate(created_at)
        );
      },
    },
    {
      title: "Action",
      fixed: 'right',
      align: "center",
      render: (_, record) => {
        return (
          <>
            <Tooltip title={"Update " + sectionName} color={"purple"} key={"update" + routeName}>
              <Button onClick={() => { edit(record._id) }}>
                <i className="fa fa-light fa-pen"></i>
              </Button>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const edit = (id) => {
    navigate(`/${routeName}/update/${id}${location.search}`);
  };

  useEffect(() => {
    const params = new URLSearchParams();

    if (pagination.current > 1) params.set("page", pagination.current);
    if (pagination.pageSize !== 20) params.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) params.set("search", debouncedSearchText);
    if (sortBy) params.set("sort", sortBy);

    navigate({ search: params.toString() }, { replace: true });
  }, [pagination.current, pagination.pageSize, debouncedSearchText, sortBy, navigate]);

  useEffect(() => {
    setLoading(true)
    fetchData(pagination, filters, sortBy)
  }, [refresh, debouncedSearchText, sortBy, pagination.current, pagination.pageSize, filters])

  const fetchData = (pagination, filters, sortValue = '') => {

    const filterActive = filters ? filters.is_active : null

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(',') : ''}` +
        `&page=${pagination.current}` +
        `&limit=${pagination.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}`,
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false)
        setList(data.data.list.docs)
        setPagination(prev => ({
          ...prev,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.data.list.totalDocs
        }));
      },
      onError: (error) => {
        console.log(error)
        ShowToast(error, Severty.ERROR)
      }
    })
  }

  const handleChange = (pag, newFilters, sorter) => {
    const { order, field } = sorter || {};

    // The brand column is nested, so its dataIndex comes back as an array
    const sortField = Array.isArray(field) ? field.join('.') : field;

    let nextSort = '';
    if (order) {
      nextSort = `${sortField}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    setFilters(newFilters || {});

    setSortBy(nextSort);

    setPagination(prev => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value)
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Management"}
              extra={
                <>
                  <Search
                    allowClear
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search By Code, Brand, Type, Referrer"
                    style={{ minWidth: 280 }}
                  />
                  <Link className="ant-btn ant-btn-default" to={`/${routeName}/add${location.search}`}>Add New {sectionName}</Link>
                </>
              }
            >
              <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>
              <div className="table-responsive customPagination">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  rowKey={(record) => record._id}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    responsive: true,
                    defaultPageSize: 20,
                    pageSizeOptions: ['10', '20', '30', '50', '100']
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>

            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default Index;
